// --------------------------------------------------------------
// Created On: 2026-10-19
// Author: Zachary Thomas
//
// Last Modified: 2026-10-30
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import { LOCAL_STORAGE_SELECTED_ASSETGROUP_ID_KEY } from "src/constants/localStorageVariables";
import React from "react";
import { UPDATE_ASSET_GROUPS_PERMISSION } from "src/constants/permissions";
import userHasPermission from "src/utilities/userHasPermission";
import TitleBar from "src/components/TitleBar/TitleBar";
import styles from "./GroupAndEditSelector.module.scss";

// Dropdown for selecting asset group and for editing system control settings.
export default function GroupAndEditSelector(props: Props): Component {
  // Save the asset group selection to local storage, so that the users selection can be kept between page changes.
  function selectAssetgroup(assetgroupId: number): void {
    localStorage.setItem(LOCAL_STORAGE_SELECTED_ASSETGROUP_ID_KEY, String(assetgroupId));
    props.onSelectAssetgroup(assetgroupId);
  }

  // Gets the correct styling string based on how much space the asset group selector should use.
  function getAssetGroupSelectorColumnStyling(inEditMode: boolean): string {
    if (!userHasPermission([[UPDATE_ASSET_GROUPS_PERMISSION]])) {
      return "col-12";
    } else if (inEditMode) {
      return "col-7 col-md-8 col-lg-9";
    } else {
      return "col-8 col-lg-10";
    }
  }

  return (
    <div>
      <div className="p-4 w-100">
        <div className="row">
          {/* Assetgroup select. */}
          <div className={getAssetGroupSelectorColumnStyling(props.inEditMode)}>
            <div className="shadow-sm">
              <TitleBar title="Select Asset Group" isShort />
              <select
                data-test="assetgroup-groupselector"
                className={`${styles.selectorSelect} ${styles.roundingPriority} form-select`}
                value={props.assetgroupId}
                disabled={props.loading || props.disabled || props.inEditMode}
                onChange={(e) => selectAssetgroup(parseInt(e.target.value, 10))}
              >
                <option value="0">All Available Assets</option>
                {props.assetgroups.map((assetgroup) => (
                  <option value={assetgroup.assetgroupId} key={assetgroup.assetgroupId}>
                    {assetgroup.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* System control controls, laid out as a card so they match the selector and the other dashboards. */}
          {userHasPermission([[UPDATE_ASSET_GROUPS_PERMISSION]]) && (
            <div className={props.inEditMode ? "col-5 col-md-4 col-lg-3" : "col-4 col-lg-2"}>
              <div className={`shadow-sm h-100 ${styles.commandCard}`}>
                {/* A shorter title on phones so it fits on one line and stays level with the selector's title. */}
                <div className="d-md-none">
                  <TitleBar title="Components" isShort />
                </div>
                <div className="d-none d-md-block">
                  <TitleBar title="Manage Components" isShort />
                </div>
                {/* Save and cancel are icon-only on phones, where there is no room for both words side by side. */}
                <div className={styles.commandButtons}>
                  {!props.inEditMode && (
                    <button
                      data-test="system-control-edit-button"
                      className={`${styles.commandButton} btn btn-primary`}
                      type="button"
                      disabled={props.loading || props.disabled}
                      onClick={() => props.onEditSystemControl()}
                    >
                      <i className="fa fa-fw fa-pencil d-none d-md-inline-block" /> Edit
                    </button>
                  )}
                  {props.inEditMode && (
                    <button
                      data-test="system-control-save-button"
                      className={`${styles.commandButton} btn btn-primary`}
                      type="button"
                      title="Save"
                      aria-label="Save"
                      disabled={props.loading || props.disabled}
                      onClick={() => props.onSaveEditSystemControl()}
                    >
                      <i className="fa fa-fw fa-check" />
                      <span className="d-none d-md-inline"> Save</span>
                    </button>
                  )}
                  {props.inEditMode && (
                    <button
                      data-test="system-control-cancel-button"
                      className={`${styles.commandButton} btn btn-secondary`}
                      type="button"
                      title="Cancel"
                      aria-label="Cancel"
                      disabled={props.loading || props.disabled}
                      onClick={() => props.onCancelEditSystemControl()}
                    >
                      <i className="fa fa-fw fa-times" />
                      <span className="d-none d-md-inline"> Cancel</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface Props {
  assetgroups: DashboardAssetgroup[];
  assetgroupId: number;
  inEditMode: boolean;
  disabled: boolean;
  changesHaveBeenMade: boolean;
  loading: boolean;
  onSelectAssetgroup: (assetgroupId: number) => void;
  onEditSystemControl: () => void;
  onSaveEditSystemControl: () => void;
  onCancelEditSystemControl: () => void;
}

interface DashboardAssetgroup {
  assetgroupId: number;
  name: string;
}
